// OrderModal.jsx – Auftrag bestellen
import React, { useState } from 'react';
import { calcPrice, packSummary, packLine, fmt, LIEFERKOSTEN, RETOUCH_PRICE } from '../utils/pricing';
import { CopiesInput } from './CopiesInput';
import './Modal.css';

const API = 'https://editor.bandeleart.ch';

const COUNTRIES = [
  'Schweiz', 'Deutschland', 'Österreich', 'Liechtenstein',
  'Frankreich', 'Italien', 'Luxemburg', 'Belgien', 'Niederlande',
  'Anderes Land',
];



const ROW = { display: 'flex', justifyContent: 'space-between', marginBottom: 4 };
const LABEL = { color: '#555', fontSize: 14 };
const VAL   = { fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' };

const BOX_NAME_MAX = 40;

export function OrderModal({ personsCount, retouchCount = 0, session, onClose, onOrderSubmitted }) {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [form, setForm] = useState({
    name: '', street: '', zip: '', city: '', country: 'Schweiz',
    email: session?.email || '', phone: '', copies: 1, remark: '',
    boxName: (session?.treeName || '').slice(0, BOX_NAME_MAX),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const n = personsCount;
  const preis = calcPrice({ persons: n, copies: form.copies, retouchCount });

  const upd = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.boxName.trim()) {
      setError('Bitte gib den Namen ein, der auf die Spielebox gedruckt wird.');
      return;
    }
    if (!form.name.trim() || !form.street.trim() || !form.zip.trim() ||
        !form.city.trim() || !form.email.includes('@')) {
      setError('Bitte alle Pflichtfelder (*) ausfüllen.');
      return;
    }
    setLoading(true);
    try {
      let token = '';
      try { token = JSON.parse(localStorage.getItem('stammbaum_user_session') || 'null')?.token || ''; } catch {}
      const res = await fetch(`${API}/api/tree/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          treeCode: session.treeCode,
          orderData: {
            boxName: form.boxName.trim(),
            name:    form.name,
            address: `${form.street}, ${form.zip} ${form.city}, ${form.country}`,
            street:  form.street,
            zip:     form.zip,
            city:    form.city,
            country: form.country,
            email:   form.email,
            phone:   form.phone.trim(),
            copies:  form.copies,
            remark:  form.remark.trim(),
            personsCount: n,
            retouchCount,
            retouchPreis:  preis.retouch,
            auftragsPreis: preis.retouch,          // Auftragspreis = nur noch Bildbearbeitung von Hand
            packs:         packSummary(preis.packs),
            packPreis:     preis.packPrice,
            druckTotal:    preis.druck,
            lieferkosten:  preis.liefer,
            totalGesamt:   preis.total,
          },
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Fehler beim Absenden. Bitte nochmals versuchen.');
        return;
      }
      setStep('success');
      onOrderSubmitted();
    } catch {
      setError('Server nicht erreichbar. Bitte später nochmals versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // ── Bestätigungsscreen ────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 480, padding: '36px 32px', textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}></div>
          <h2 style={{ fontSize: 22, marginBottom: 8 }}>Auftrag erhalten!</h2>
          <p style={{ color: '#666', fontSize: 15, marginBottom: 28, lineHeight: 1.6 }}>
            Vielen Dank für deinen Auftrag. Dein Stammbaum ist nun gesperrt und wird nicht mehr verändert.
          </p>

          <div style={{ textAlign: 'left', background: '#f5f7ff', borderRadius: 12, padding: '20px 24px', marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1540dc', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
              Nächste Schritte
            </div>
            {[
              { num: '1', title: 'Auftragsüberprüfung', desc: 'Wir prüfen deinen Stammbaum und melden uns bei Fragen.' },
              { num: '2', title: 'Rechnung erhalten', desc: 'Du erhältst eine detaillierte Rechnung per E-Mail.' },
              { num: '3', title: 'Auftrag ausführen', desc: 'Nach Zahlungseingang wird dein Stammbaum gedruckt und versendet.' },
            ].map(s => (
              <div key={s.num} style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: 28, height: 28, borderRadius: '50%', background: '#1540dc',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13,
                }}>{s.num}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{s.title}</div>
                  <div style={{ color: '#666', fontSize: 13, lineHeight: 1.5 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <button
            className="btn btn--primary"
            onClick={onClose}
            style={{ width: '100%', padding: '13px 0', fontSize: 15, borderRadius: 8 }}
          >
            Schliessen
          </button>
        </div>
      </div>
    );
  }

  // ── Bestellformular ───────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520, padding: '28px 28px 24px' }}>
        <div className="modal-header" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 20 }}>Stammbaum bestellen</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Name auf der Spielebox */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Name auf der Spielebox *
            </div>
            <input className="ws-input" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}
              type="text" placeholder="z.B. Familie Muster"
              maxLength={BOX_NAME_MAX}
              value={form.boxName} onChange={e => upd('boxName', e.target.value)} required />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: '#888', marginTop: 5, lineHeight: 1.4 }}>
              <span>Dieser Name wird so auf die Spielebox gedruckt. Bitte Schreibweise prüfen.</span>
              <span style={{ flexShrink: 0 }}>{form.boxName.length}/{BOX_NAME_MAX}</span>
            </div>
          </div>

          {/* Kontaktdaten */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Kontaktdaten
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="ws-input" style={{ margin: 0 }}
                type="text" placeholder="Vor- und Nachname *"
                value={form.name} onChange={e => upd('name', e.target.value)} required />
              <input className="ws-input" style={{ margin: 0 }}
                type="text" placeholder="Strasse und Hausnummer *"
                value={form.street} onChange={e => upd('street', e.target.value)} required />
              <div style={{ display: 'flex', gap: 10 }}>
                <input className="ws-input" style={{ margin: 0, width: 100 }}
                  type="text" placeholder="PLZ *"
                  value={form.zip} onChange={e => upd('zip', e.target.value)} required />
                <input className="ws-input" style={{ margin: 0, flex: 1 }}
                  type="text" placeholder="Ort *"
                  value={form.city} onChange={e => upd('city', e.target.value)} required />
              </div>
              <select className="ws-input" style={{ margin: 0 }}
                value={form.country} onChange={e => upd('country', e.target.value)}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="ws-input" style={{ margin: 0 }}
                type="email" placeholder="E-Mail-Adresse *"
                value={form.email} onChange={e => upd('email', e.target.value)} required />
              <input className="ws-input" style={{ margin: 0 }}
                type="tel" placeholder="Telefonnummer (freiwillig)"
                value={form.phone} onChange={e => upd('phone', e.target.value)} />
            </div>
          </div>

          {/* Anzahl Exemplare */}
          <div style={{ background: '#f8f8f8', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Anzahl Exemplare
            </div>
            <CopiesInput value={form.copies} onChange={v => upd('copies', v)} />
          </div>

          {/* Bemerkung */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Bemerkung (optional)
            </div>
            <textarea className="ws-input"
              style={{ margin: 0, width: '100%', minHeight: 80, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
              placeholder="Hast du Spezialwünsche oder Hinweise zu deinem Stammbaum? Schreib sie hier."
              maxLength={2000}
              value={form.remark} onChange={e => upd('remark', e.target.value)} />
          </div>

          {/* Preisübersicht */}
          <div style={{ background: '#f8f8f8', borderRadius: 10, padding: '14px 18px', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Preisübersicht
            </div>
            <div style={ROW}>
              <span style={LABEL}>{packLine(form.copies, preis)}</span>
              <span style={VAL}>{fmt(preis.druck)}</span>
            </div>
            {retouchCount > 0 && (
              <div style={ROW}>
                <span style={LABEL}>{retouchCount} {retouchCount === 1 ? 'Bild' : 'Bilder'} von Hand bearbeiten × {RETOUCH_PRICE} CHF</span>
                <span style={VAL}>{fmt(preis.retouch)}</span>
              </div>
            )}
            <div style={ROW}>
              <span style={LABEL}>Lieferkosten</span>
              <span style={VAL}>{fmt(LIEFERKOSTEN)}</span>
            </div>
            <div style={{ borderTop: '2px solid #1540dc', marginTop: 10, paddingTop: 10, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 800, fontSize: 16 }}>Geschätzter Gesamtpreis</span>
              <span style={{ fontWeight: 800, fontSize: 16, color: '#1540dc' }}>{fmt(preis.total)}</span>
            </div>
          </div>

          <p style={{ color: '#999', fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>
            Dies ist ein ungefährer Richtwert. Der finale Preis wird nach der Auftragsüberprüfung
            per E-Mail kommuniziert und kann je nach individuellem Aufwand abweichen.
          </p>

          {error && <p style={{ color: '#e53e3e', fontSize: 13, marginBottom: 12 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn--ghost" onClick={onClose}
              style={{ flex: 1, padding: '12px 0' }}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn--primary" disabled={loading}
              style={{ flex: 2, padding: '12px 0', fontSize: 15, borderRadius: 8 }}>
              {loading ? 'Wird gesendet…' : 'Auftrag absenden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
